import { GeckoEntryBase } from 'types/entries/geckoEntry'
import {
  RolodexEntryIndexType,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'

export interface ValueStoryTemplateData {
  name: string
  content: string
  isDefault: boolean
  [key: string]: unknown
}

export function isGeckoValueStoryTemplate(
  entry: unknown
): entry is GeckoValueStoryTemplate {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'type' in entry &&
    (entry as { type: string }).type === RolodexEntryType.VALUE_STORY_TEMPLATE
  )
}

export class GeckoValueStoryTemplate extends GeckoEntryBase<
  ValueStoryTemplateData,
  {
    site: string
    organization?: string
    sandbox?: string
  },
  RolodexEntryIndexType
> {
  public static fromJson(
    json: RolodexEntryJson<
      RolodexEntryType.VALUE_STORY_TEMPLATE,
      ValueStoryTemplateData
    >
  ): GeckoValueStoryTemplate | undefined {
    if (json.type !== RolodexEntryType.VALUE_STORY_TEMPLATE) {
      return undefined
    }

    try {
      return new GeckoValueStoryTemplate(
        json,
        json.data,
        json.tags as { site: string; organization?: string; sandbox?: string },
        json.index
      )
    } catch (error) {
      console.warn('Failed to create GeckoValueStoryTemplate from JSON:', error)
      return undefined
    }
  }

  public static create(
    templateData: ValueStoryTemplateData
  ): GeckoValueStoryTemplate {
    const now = new Date().toISOString()
    const json: RolodexEntryJson<
      RolodexEntryType.VALUE_STORY_TEMPLATE,
      ValueStoryTemplateData
    > = {
      id: '', // Will be set by rolodex
      type: RolodexEntryType.VALUE_STORY_TEMPLATE,
      data: templateData,
      tags: {},
      index: {},
      generation: 0,
      updated_at: now,
      created_at: now,
      access_restriction: null,
    }

    return new GeckoValueStoryTemplate(
      json,
      json.data,
      json.tags as { site: string; organization?: string; sandbox?: string },
      json.index
    )
  }

  private constructor(
    json: RolodexEntryJson,
    data: ValueStoryTemplateData,
    tags: { site: string; organization?: string; sandbox?: string },
    index: RolodexEntryIndexType
  ) {
    super(json, data, tags, index)
  }

  public get templateData(): ValueStoryTemplateData {
    return this.data
  }

  public get name(): string {
    return this.data.name || 'Untitled Template'
  }

  public get content(): string {
    return this.data.content || ''
  }

  public get isDefault(): boolean {
    return this.data.isDefault || false
  }
}
